import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import type { IncludedIncomeStreamView } from '../../lib/incomeStreamPresentation';

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function StreamTabButton({
  stream,
  selected,
  onClick,
}: {
  stream: IncludedIncomeStreamView;
  selected: boolean;
  onClick: () => void;
}) {
  const accent = stream.accent.main;
  const label = stream.payor.length <= 32 ? stream.payor : `${stream.payor.slice(0, 29)}…`;

  return (
    <Tooltip title={`${stream.payor} · ${formatMoney(stream.monthlyAmount)}/mo`} arrow>
      <Box
        component={motion.button}
        type="button"
        onClick={onClick}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.97 }}
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: { xs: 140, sm: 0 },
          border: 'none',
          cursor: 'pointer',
          borderRadius: 2.5,
          px: { xs: 1, sm: 1.5 },
          py: 1.1,
          bgcolor: 'transparent',
          color: selected ? accent : 'text.secondary',
          fontFamily: 'inherit',
          fontSize: '0.8125rem',
          fontWeight: selected ? 700 : 500,
          outline: 'none',
          zIndex: selected ? 2 : 1,
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${alpha(accent, 0.5)}`,
          },
        }}
      >
        {selected ? (
          <Box
            component={motion.span}
            layoutId="income-stream-tab-pill"
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 2.5,
              bgcolor: alpha(accent, 0.14),
              border: `1px solid ${alpha(accent, 0.4)}`,
              boxShadow: `0 4px 14px ${alpha(accent, 0.22)}`,
            }}
          />
        ) : null}
        <Box
          component="span"
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: accent,
              flexShrink: 0,
            }}
          />
          <Box component="span" sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography component="span" variant="body2" sx={{ fontWeight: 'inherit', display: 'block', lineHeight: 1.2 }}>
              {label}
            </Typography>
            <Typography component="span" variant="caption" sx={{ color: selected ? accent : 'text.secondary', fontWeight: 600 }}>
              {formatMoney(stream.monthlyAmount)}/mo
            </Typography>
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}

export function IncomeStreamTabBar({
  streams,
  selectedPayorKey,
  onSelect,
}: {
  streams: IncludedIncomeStreamView[];
  selectedPayorKey: string;
  onSelect: (payorKey: string) => void;
}) {
  if (streams.length <= 1) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: `repeat(${streams.length}, minmax(0, 1fr))` },
        gap: 0.75,
        mb: 1.5,
        p: 0.5,
        borderRadius: 2.5,
        bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.03 : 0.08),
      }}
      role="tablist"
      aria-label="Income streams included in monthly total"
    >
      {streams.map((stream) => (
        <Box key={stream.payorKey} role="presentation">
          <StreamTabButton
            stream={stream}
            selected={stream.payorKey === selectedPayorKey}
            onClick={() => onSelect(stream.payorKey)}
          />
        </Box>
      ))}
    </Box>
  );
}
